<template>
  <div>
    <div
      v-if="isLoading"
      v-loading="isLoading"
      class="mt-5"
    />

    <template v-else>
      <el-card
        shadow="never"
        class="w-100 mb-3"
      >
        <ConsignmentHeaderPeek :consignment="consignment" />
      </el-card>
      <el-alert
        v-if="consignment.con_rtp_type > 0"
        type="error"
        class="w-100 mb-3"
        :closable="false"
      >
        This consignment has been charged to a 3rd party account and is not billable. The account number is: {{ $_.get(consignment, 'con_rtp_account') }}
      </el-alert>
      <el-alert
        v-if="consignment.account_own_carrier"
        type="error"
        class="w-100 mb-3"
        :closable="false"
      >
        This consignment is using a carrier account that is owned by the consignor. This is not billable.
      </el-alert>
      <el-alert
        v-if="consignment.con_rtp_type === -1"
        type="warning"
        class="w-100 mb-3"
        :closable="false"
      >
        This consignment has had it's cost centre set to {{ $_.get(consignment, 'con_rtp_account') }}
      </el-alert>
      <el-card
        shadow="never"
        class="w-100 mb-3"
      >
        <ConsignmentPeek
          ref="peek"
          :consignment="consignment"
        />
      </el-card>
    </template>
    <el-dialog
      title="Cancel Consignment"
      :visible.sync="showCancelDialog"
      :close-on-click-modal="false"
      width="30%"
    >
      <span>
        Please enter a cancellation reason
      </span>
      <el-input
        v-model="consignmentCancelReason"
        type="textarea"
        placeholder="Cancellation Reason"
      />
      <span
        slot="footer"
        class="dialog-footer"
      >
        <el-button @click="showCancelDialog = false">
          Cancel
        </el-button>
        <el-button
          type="primary"
          @click="handleClose"
        >
          Confirm
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Vue from 'vue'
import ConsignmentHeaderPeek from '@/components/Application/Freight/Consignments/Peek/Header'
import ConsignmentPeek from '@/components/Application/Freight/ConsignmentPeek'
import Request, { GET, POST } from '@/plugins/sdk/Request'
import PDFDialog from '@/components/Application/Freight/Consignments/Attachments/PDFDialog'

export default {
  components: {
    ConsignmentHeaderPeek,
    ConsignmentPeek
  },
  data () {
    return {
      breadcrumb: {
        name: 'Details',
        route: { name: 'freight-consignments-v3-consignment-details', params: { consignmentId: this.$route.params.consignmentId } }
      },
      isLoading: true,
      consignment: null,
      pdfDialogInstance: null,
      pdfDialogVisible: false,
      cancelRestoreTitle: '',
      cancelRestoreIcon: '',
      isCancellingConsignment: false,
      showCancelDialog: false,
      consignmentCancelReason: '',
      isReindexingConsignment: false,
      alfred: [
        {
          name: 'Print Label',
          description: 'Print the consignment label',
          keywords: ['consignment', 'print', 'label'],
          icon: ['fal', 'print'],
          hidden: false,
          handler: () => {
            this.printLabel()
          }
        },
        {
          name: 'Print Connote',
          description: 'Print the consignment connote',
          keywords: ['consignment', 'print', 'connote'],
          icon: ['fal', 'print'],
          hidden: false,
          handler: () => {
            this.printConnote()
          }
        },
        {
          name: 'Print Manifest',
          description: 'Print the consignment manifest',
          keywords: ['consignment', 'print', 'manifest'],
          icon: ['fal', 'print'],
          hidden: false,
          handler: () => {
            this.printManifest()
          }
        },
        {
          name: 'Manifest Consignment',
          description: 'Manifest the consignment',
          keywords: ['consignment', 'manifest'],
          icon: ['fal', 'book'],
          hidden: false,
          handler: () => {
            this.manifestConsignment()
          }
        },
        {
          name: 'Cancel/Restore Consignment',
          description: 'Cancel or restore the consignment',
          keywords: ['consignment', 'cancel', 'restore'],
          icon: this.cancelRestoreIcon,
          hidden: false,
          handler: () => {
            this.cancelRestoreConsignment()
          }
        },
        {
          name: 'Search Sync',
          description: 'Add the consignment to the search reindex queue',
          keywords: ['consignment', 'search', 'sync', 'reindex'],
          icon: ['fal', 'search-plus'],
          hidden: false,
          handler: () => {
            this.reindexConsignment()
          }
        },
        {
          name: 'Add Tracking Event',
          description: 'Add a tracking event to the consignment',
          keywords: ['consignment', 'tracking', 'event', 'add'],
          icon: ['fal', 'plus'],
          hidden: false,
          handler: () => {
            this.$refs.peek.$refs.trackingCard.createTrackingEvent()
          }
        },
        {
          name: 'Manage Tracking Subscriptions',
          description: 'Manage tracking subscriptions for the consignment',
          keywords: ['consignment', 'tracking', 'subscriptions', 'manage'],
          icon: ['fal', 'message'],
          hidden: false,
          handler: () => {
            this.$refs.peek.$refs.trackingCard.createTrackingSubscription()
          }
        },
        {
          name: 'View Track My Parcel',
          description: 'View the tracking information for the consignment',
          keywords: ['consignment', 'tracking', 'track', 'parcel'],
          icon: ['fal', 'eye'],
          hidden: false,
          handler: () => {
            this.$refs.peek.$refs.trackingCard.openTrackMyParcel()
          }
        },
        {
          name: 'Open Carrier Tracking Page',
          description: 'Open the carrier tracking page for the consignment',
          keywords: ['consignment', 'tracking', 'carrier', 'tracking', 'page'],
          icon: ['fal', 'external-link'],
          hidden: false,
          handler: () => {
            this.$refs.peek.$refs.trackingCard.openCarrierTracking()
          }
        }
      ]
    }
  },
  computed: {
    title () {
      return this.consignment ? `${this.consignment.con_full}` : 'Consignment'
    },
    actions () {
      const actions = []

      if (this.consignment instanceof this.$sdk.consignment) {
        actions.push(
          [
            {
              name: 'Refresh',
              icon: ['fal', 'sync'],
              event: this.loadConsignment
            }
          ]
        )
        actions.push(
          [
            {
              hidden: this.consignment && this.consignment.edi,
              name: 'Manifest',
              icon: ['fal', 'book'],
              event: this.manifestConsignment
            }
          ]
        )
        actions.push(
          [
            {
              name: this.cancelRestoreTitle,
              icon: this.cancelRestoreIcon,
              event: this.cancelRestoreConsignment,
              loading: this.isCancellingConsignment
            }
          ]
        )
        actions.push(
          [
            {
              name: 'Search Sync',
              icon: ['fal', 'search-plus'],
              event: this.reindexConsignment,
              loading: this.isReindexingConsignment
            }
          ]
        )
        actions.push(
          [
            {
              hidden: true,
              name: 'Print Label',
              icon: ['fal', 'file'],
              event: this.printLabel
            },
            {
              hidden: true,
              name: 'Print Connote',
              icon: ['fal', 'file'],
              event: this.printConnote
            },
            {
              hidden: true,
              name: 'Print Manifest',
              icon: ['fal', 'file'],
              event: this.printManifest
            }
          ]
        )
      }

      return actions
    }
  },
  watch: {
    consignment: function (newConsignment, oldConsignment) {
      this.isCancelled()
    }
  },
  created () {
    this.loadConsignment()
  },
  methods: {
    async reindexConsignment () {
      this.isReindexingConsignment = true
      const request = Object.assign(new Request(), {
        _pathname: `/public/search/create/TIGFRE/consignment/${this.consignment.id}`,
        method: GET
      })
      let res = await fetch(await request.build())
      if (res.ok) {
        this.$message({ message: 'Consignment added to reindex queue', type: 'success' })
      } else {
        this.$message({ message: 'Failed to add consignment to reindex queue', type: 'error' })
      }
      this.isReindexingConsignment = false
    },
    isCancelled () {
      if (this.consignment && ['-1', '-6', '-7', '-4', '-8'].includes(this.consignment.con_type)) {
        this.cancelRestoreTitle = 'Restore'
        this.cancelRestoreIcon = ['fal', 'undo']
      } else {
        this.cancelRestoreTitle = 'Cancel'
        this.cancelRestoreIcon = ['fal', 'trash']
      }
    },
    loadConsignment () {
      this.isLoading = true
      this
        .$sdk
        .consignment
        .read(this.$route.params.consignmentId, request => {
          request.include = [
            'account.investigator',
            'account.portfolio.profile',
            'carrier',
            'service',
            'team.account',
            'items',
            'trackings.scan.proof_of_delivery',
            'trackings.level',
            'trackings.level.type',
            'trackings.consignment_item',
            'pickup',
            'items',
            'edi',
            'items.contents',
            'items.dg',
            'tracking_errors',
            'tracking_subscriptions',
            'enquiries',
            'quoted_pricing',
            'con_pricing',
            'inv_pricing',
            'features',
            'watchlists'
          ]
        })
        .then(res => { this.consignment = res })
        .finally(() => { this.isLoading = false })
    },
    async cancelRestoreConsignment () {
      if (this.consignment.con_type === '-6') {
        this.isCancellingConsignment = true
        this.consignment.con_type = 0
        let note = new this.$sdk.note()
        note.content = '<b>Restored Consignment</b>'
        note.resource_uuid = this.consignment.id
        note.resource = 'consignment'
        await this.consignment.save().then(() => this.$message({ message: 'Consignment Restored', type: 'success' }))
        await note.create().then(() => this.$message({ message: 'Note Added', type: 'success' }))
        this.isCancellingConsignment = false
        this.isCancelled()
      } else {
        this.showCancelDialog = true
      }
    },
    async handleClose () {
      this.showCancelDialog = false
      this.consignment.con_type = -6
      let note = new this.$sdk.note()
      note.content = '<b>Cancelled Consignment</b> <br> Reason: ' + this.consignmentCancelReason
      note.resource_uuid = this.consignment.id
      note.resource = 'consignment'
      console.log(note.content)
      console.log(note.toJSON())
      this.isCancellingConsignment = true
      await this.consignment.save().then(() => this.$message({ message: 'Consignment Cancelled', type: 'success' }))
      await note.create().then(() => this.$message({ message: 'Note Added', type: 'success' }))
      this.isCancellingConsignment = false
      this.isCancelled()
    },
    async manifestConsignment () {
      const request = Object.assign(new Request(), {
        _pathname: `/legacy/manifest?consignment=${this.consignment.id}`,
        method: POST
      })
      let res = await fetch(await request.build())

      if (res.ok) {
        this.$message({ message: 'Consignment Manifested', type: 'success' })
      } else {
        this.$message({ message: 'Manifest Failed', type: 'error' })
      }
    },
    async printLabel () {
      this.$message({ message: 'Generating Label...', type: 'info' })
      const request = Object.assign(new Request(), {
        _pathname: `/legacy/label?consignment=${this.consignment.id}`,
        method: GET
      })
      let res = await fetch(await request.build())
      let data = await res.json()

      if (data.label_pdf) {
        this.openPDFDialog(data.label_pdf, 'label')
      } else {
        this.$message.error('No Label Paperwork Generated')
      }
    },
    async printConnote () {
      this.$message({ message: 'Generating Connote...', type: 'info' })
      const request = Object.assign(new Request(), {
        _pathname: `/legacy/label?consignment=${this.consignment.id}`,
        method: GET
      })
      let res = await fetch(await request.build())
      let data = await res.json()

      if (data.connote_pdf) {
        this.openPDFDialog(data.connote_pdf, 'connote')
      } else {
        this.$message.error('No Connote Paperwork Generated')
      }
    },
    async printDG () {
      this.$message({ message: 'Generating Dangerous Goods...', type: 'info' })
      const request = Object.assign(new Request(), {
        _pathname: `/legacy/label?consignment=${this.consignment.id}`,
        method: GET
      })
      let res = await fetch(await request.build())
      let data = await res.json()

      if (data.dg_pdf) {
        this.openPDFDialog(data.dg_pdf, 'dg')
      } else {
        this.$message.error('No Dangerous Goods Paperwork Generated')
      }
    },
    async printInvoice () {
      this.$message({ message: 'Generating Invoice...', type: 'info' })
      const request = Object.assign(new Request(), {
        _pathname: `/legacy/label?consignment=${this.consignment.id}`,
        method: GET
      })
      let res = await fetch(await request.build())
      let data = await res.json()

      if (data.invoice_pdf) {
        this.openPDFDialog(data.invoice_pdf, 'invoice')
      } else {
        this.$message.error('No Invoice Paperwork Generated')
      }
    },
    openPDFDialog (data, type) {
      const PDFDialogClass = Vue.extend(PDFDialog)

      this.pdfDialogInstance = new PDFDialogClass({
        propsData: {
          isShow: true,
          pdfData: data,
          title: type === 'label' ? 'Label' : type === 'connote' ? 'Connote' : type === 'dg' ? 'Dangerous Goods' : type === 'invoice' ? 'Invoice' : 'Manifest'
        }
      })

      this
        .pdfDialogInstance
        .$on('destroy', () => {
          this.pdfDialogVisible = false
        })

      this
        .pdfDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.pdfDialogInstance.$el)

      this.pdfDialogVisible = true
    },
    async printManifest () {
      const request = Object.assign(new Request(), {
        _pathname: `/legacy/manifest?consignment=${this.consignment.id}`,
        method: GET
      })
      let res = await fetch(await request.build())
      let data = await res.json()

      if (data.manifest_pdf) {
        window.open(atob(data.manifest_pdf), '_blank')
      } else {
        this.$message.error('No Manifest Paperwork Generated')
      }
    }
  }
}
</script>
